import {
	applyCachedProfile,
	capturePreMatchSnapshot,
	fetchFilterPreference,
	MATCH_MAPLIBRE_FILTER_KEY,
	restorePreMatchSnapshot
} from '$mapapp/MapApp/FilterControl/filter.controller.svelte'
import { appState, MAP_BREADCRUMBS_CONTEXT } from '$mapapp/stores/appstate.svelte'
import { fakState } from '$mapapp/stores/fak.svelte'
import { Matching, type MatchingOptions } from './matching.svelte'
import { DEFAULT_RADIUS_KM } from './matching-config'
import { isValidMatchingRadius } from '$mapapp/MapApp/SlideIn/Views/Matching/utils'
import {
	syncFakParams,
	syncMarkerParam,
	clearAllMarkerParams,
	syncMatchParam,
	syncRadiusParam,
	clearMatchParam
} from './url.svelte'
import { FiniteStateMachine, type Action } from 'runed'
import type { MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import type { MapSearchOption } from '$mapapp/MapApp/SlideIn/SearchRoutingHistory/MapSearch/MapSearch.Model'
import type { slideInModes } from '$mapapp/data/data.model'
import { breadcrumbs, pushBreadcrumb } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
export type States = 'idle' | 'routing' | 'fak' | slideInModes
export type SetEvents =
	| 'set_routing'
	| 'set_project'
	| 'set_cluster'
	| 'set_lot'
	| 'set_pop'
	| 'set_matching'
	| 'set_minilot'
	| 'set_request'
	| 'set_returnFreight'
	| 'set_fak'

type SpecialEvents = 'close_fak'

export type AllEvents = 'reset' | SetEvents | SpecialEvents

const ENTRY_STATE = 'idle'

let is_initial_reset = true

/** when switching from any non-idle state to routing,
 * store state (state, selectedId), clear state
 * then switch on routing
 * */

// TODO: handle memoization / "caching" in appState

function enforce_sidebar() {
	if (appState.isRoutePlanerOpened) {
		appState.isRoutePlanerOpened = false
	}
	if (!appState.isSlideInOpened) {
		appState.isSlideInOpened = true
	}
}

function enforce_routing() {
	if (!appState.isSlideInOpened) {
		appState.isSlideInOpened = true
	}

	if (!appState.isRoutePlanerOpened) {
		appState.isRoutePlanerOpened = true
	}
}

function enforce_fak() {
	if (!fakState.isOpen) {
		fakState.isOpen = true
	}
}

function close_fak() {
	fakState.close()
	syncFakParams(null, null)

	// Restore marker URL state if returning to a marker state
	restore_prev_state()

	return appState.previous_state ?? ('idle' as States)
}

function capture_prev_state(state: States) {
	appState.previous_state = state
}

function restore_prev_state() {
	const prevState = appState.previous_state
	// 'cluster' deliberately omitted — clusters have no URL slot (MARKER_PARAMS =
	// pop/lot/project); writing `?cluster=<id>` would leak the synthetic id and
	// require a separate cleanup pass when the cluster is closed.
	if (prevState && ['pop', 'lot', 'project'].includes(prevState) && appState.selectedId) {
		syncMarkerParam(prevState as slideInModes, appState.selectedId)
	}
}

function initializeMatching(event: {
	id: string
	type: 'pop' | 'lot' | 'miniLot'
	options: MatchingOptions
}): States {
	if (!event?.id && !event?.options?.miniLot) {
		console.error('id or minilot required')
		return fsm.current as States
	}

	const matchParamId = event.type + (event.id ? '-' + event.id : '')
	syncMatchParam(matchParamId, event?.options?.profileId, event?.options?.miniLot)

	/**
	 * surface the radius from the very start of matching — the default 30km included — so the
	 * shareable URL always reflects the active search radius, not only after the user edits it
	 */
	if (event.options?.radius && !isValidMatchingRadius(event.options.radius.to)) {
		event.options.radius.to = DEFAULT_RADIUS_KM
	}
	syncRadiusParam(event?.options?.radius?.to ?? DEFAULT_RADIUS_KM)

	// runed self-transitions (re-match while already in matching) skip
	// matching._exit, so close any active popup here to catch them too
	appState.closePopup()

	// re-entry from matching itself (slider, profile re-match) keeps the new
	// marker as the back-nav target instead of capturing 'matching'
	if (fsm.current === 'matching' && event.type !== 'miniLot') {
		capture_prev_state(event.type as States)
	} else {
		capture_prev_state(fsm.current)
	}

	appState.matching?.destroy?.()
	appState.matching = new Matching(event.type, event.id, event.options)

	/**
	 * only push `Matching` once per session entry.
	 *
	 * a runed self-transition (re-match while already in matching — slider, profile
	 * switch, minilot, "Match finden" pressed on the secondary slide-in) tears down
	 * and rebuilds the {@link Matching} instance but stays in the same outer fsm
	 * state. without this guard the crumb chain accumulates `Matching` on every
	 * re-match.
	 */
	if (fsm.current !== 'matching') {
		addBreadcrumb('Matching')
	}
	return 'matching' as States
}

/** HINT: this sets are modelled after the fsm marker_handler,
 * but with a modifier: we stay withing matching, hence
 * we need to use local state for selected Marker etc
 * call it "state interception" if you like
 */

function set_temporary_view(event: { id: string; type: string }) {
	appState.matching?.fsm.send('set_secondary', event)
}

/**
 * Enter the matching secondary view from a marker_handler `set_<type>` event and push the
 * corresponding crumb. First entry from `result`/`results` appends; chained
 * secondary→secondary clicks replace the last crumb so the chain stays one-deep — back-nav
 * from secondary always pops exactly one (see {@link Matching.handleMatchingHistory}).
 */
function enter_secondary(event: { id: string; type?: string }, title: string): States {
	const wasInSecondary = appState.matching?.fsm.current === 'secondary'
	set_temporary_view(event as { id: string; type: string })
	updateBreadcrumbs({ ...event, replaceLastBreadcrumb: wasInSecondary }, title)
	return 'matching'
}

// we want to intercept if its match, but not when we trigger goBack to return to a marker
function is_matching(event: { id: string; type: string; modifier?: 'exit_matching' }) {
	return fsm.current === 'matching' && event.modifier !== 'exit_matching'
}

function is_matching_fsm_in_result_or_secondary_state() {
	return ['result', 'secondary'].includes(appState.matching?.fsm.current ?? '')
}

function is_exiting_secondary_or_result_state(event: { id: string; type?: string, modifier?: 'exit_secondary' | 'exit_result' }) {
	return ['exit_secondary', 'exit_result'].includes(event.modifier ?? '')
}

	/**
	 * matching fsm has secondary slide_in opened (at the moment result or secondary state)
	 * and we don't want to exit from the current result/secondary matching state
 	 */
function is_secondary_or_result(event: { id: string; type?: string; modifier?: 'exit_secondary' | 'exit_result' }) {
	return is_matching_fsm_in_result_or_secondary_state() && !is_exiting_secondary_or_result_state(event)
}

export function addBreadcrumb(title: string) {
	pushBreadcrumb(
		{
			title,
			data: undefined as never,
			callback: (_data, index) => {
				const steps = breadcrumbs.get(MAP_BREADCRUMBS_CONTEXT).length - 1 - index
				if (steps > 0) {
					for (let i = 0; i < steps; i++) {
						appState.goBack()
					}
				}
			}
		},
		MAP_BREADCRUMBS_CONTEXT
	)
}

// HINT: in order keep user trapped in matching, we need to capture and handle set events

type BreadcrumbEvent = {
	/** Currently unused inside {@link updateBreadcrumbs} — kept optional so callers
	 * that already pass an id (set_pop/set_lot/set_request/set_cluster/set_project)
	 * still satisfy the type without any explicit cast. */
	id?: string
	replaceLastBreadcrumb?: boolean
	replaceAllBreadcrumbs?: boolean
}

function updateBreadcrumbs(event: BreadcrumbEvent, title: string) {
	// const completeTitle = `${title}: ${event.id}`
	const completeTitle = title

	// we need a mechanism to say how to replace the last state - when we click on a marker, we want to replace the last state
	// when we click via a project or cluster, we don't want to replace the last state
	if (event.replaceLastBreadcrumb) {
		breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
	} else if (event.replaceAllBreadcrumbs) {
		breadcrumbs.clear(MAP_BREADCRUMBS_CONTEXT)
	}

	addBreadcrumb(completeTitle)
}

const marker_handler = {
	set_pop: (event) => {
		if (!event?.id) {
			console.error('Pop ID is required')
			return
		}

		if (is_matching(event) && !is_secondary_or_result(event)) {
			return enter_secondary(event, 'Standort')
		}

		updateBreadcrumbs(event, 'Standort')
		appState.selectMarker(event.id, event?.flyTo)
		// TODO: move url state handler intp selectMarker method
		syncMarkerParam('pop', event.id)

		return 'pop'
	},
	set_lot: (event) => {
		if (!event?.id) {
			console.error('Lot ID is required')
			return
		}

		// when we click on the edit button in the secondary slide in, we want to open the lot view, not the secondary
		if (is_matching(event) && !is_secondary_or_result(event)) {
			return enter_secondary(event, 'Position')
		}

		updateBreadcrumbs(event, 'Position')
		appState.selectMarker(event.id, event?.flyTo)
		syncMarkerParam('lot', event.id)

		return 'lot'
	},
	set_request: (event) => {
		if (!event?.id) {
			console.error('Request ID is required')
			return
		}

		if (is_matching(event)) {
			return enter_secondary(event, 'Anfrage')
		}

		updateBreadcrumbs(event, 'Anfrage')
		appState.selectMarker(event.id, event?.flyTo)
		syncMarkerParam('request', event.id)

		return 'request'
	},
	set_cluster: (event) => {
		if (!event?.id) {
			console.error('Cluster ID is required')
			return
		}

		if (is_matching(event)) {
			return enter_secondary(event, 'Cluster')
		}

		updateBreadcrumbs(event, 'Cluster')
		appState.selectedId = event.id
		// cluster ids are synthetic and not in allMarkers, so selectedMarker would
		// otherwise stay pointed at the previously-clicked pop/lot — center/fly
		// controls would then target the stale marker. clear it explicitly.
		appState.selectedMarker = undefined
		if (event?.children) {
			appState.selectChildren(event.children)
		}
		// clusters have no URL slot (MARKER_PARAMS = pop/lot/project); clear any
		// surviving marker param so the URL reflects cluster selection.
		clearAllMarkerParams()
		return 'cluster'
	},
	set_project: (event) => {
		if (!event?.id) {
			console.error('Project ID is required')
			return
		}

		if (is_matching(event)) {
			return enter_secondary(event, 'Projekt')
		}

		updateBreadcrumbs(event, 'Projekt')
		appState.selectMarker(event.id, event?.flyTo)
		syncMarkerParam('project', event.id)

		return 'project'
	}
	// oxlint-disable-next-line typescript-eslint/no-explicit-any
} as const satisfies Partial<Record<SetEvents, States | ((event: any) => States | void)>>

const fak_handler = {
	// TODO: is setting FaK globally permitted?
	set_fak: (event) => {
		// soft_reset() // clears markers
		// TODO: add validation
		// FIXME: enable guard after case of entering with no project id but with lot id is defined
		if (!event?.projectId) {
			capture_prev_state(fsm.current)
			console.warn('Project ID is required to open FaK')
			// return appState?.previous_state ?? ('idle' as States)
		}
		if (event?.projectId || event?.lotId) {
			fakState.open({ projectId: event.projectId || null, lotId: event.lotId || null })
			clearAllMarkerParams()
			syncFakParams(event.projectId || null, event.lotId || null)
			return 'fak' as States
		}
		return appState.previous_state ?? ('idle' as States)
	}
	// oxlint-disable-next-line typescript-eslint/no-explicit-any
} as const satisfies Partial<Record<SetEvents, States | ((event: any) => States | void)>>

const global_handler = {
	...marker_handler,
	...fak_handler,
	set_routing: (event) => {
		if (event?.start) {
			// start can be a MapMarker (from marker click) or a MapSearchOption (from search selection)
			if ('markerId' in event.start) {
				// MapMarker → convert to MapSearchOption
				const m = event.start as MapMarker
				appState.route.source = {
					id: m.markerId,
					label: m.objectLabel || m.addressLabel || m.objectId,
					address: m.addressLabel,
					type: m.type,
					typeDetails: m.filterType?.[0],
					coordinates: { lat: m.lat, lon: m.lon }
				} as MapSearchOption
			} else {
				// already a MapSearchOption (from search bar)
				appState.route.source = event.start as MapSearchOption
			}
			appState.addToSearchHistory(appState.route.source)
		}
		return 'routing' as States
	},

	set_returnFreight: 'returnFreight',
	set_matching: initializeMatching as Action<States>,
	set_minilot: 'minilot' as States

	// oxlint-disable-next-line typescript-eslint/no-explicit-any
} as const satisfies Record<SetEvents, States | ((event: any) => States | void)>

export const fsm = new FiniteStateMachine<States, AllEvents>(ENTRY_STATE, {
	idle: {
		_enter: () => {
			clear_all_states()
		},
		...global_handler
	},
	routing: {
		_enter: () => {
			appState.deselectMarker()
			enforce_routing()
		},
		_exit: () => {
			// route + endpoint markers belong to the routing session — purge on any
			// transition out so they don't bleed into matching, lot/pop detail, etc.
			appState.clearAdHocMarkers()
			appState.route.clearCoordinates()
		},
		reset: reset,
		...global_handler
	},
	project: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	cluster: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	pop: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	lot: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	matching: {
		/**
		 * Apply the cached matching profile synchronously so the FilterPanel
		 * never paints stale filters for one frame; then kick off an async
		 * refresh against /maplibre-match-filter. Cache miss on cold start
		 * falls through to the async fetch only (brief flash; acceptable).
		 *
		 * re-match (set_matching while already in matching) is a runed
		 * self-transition, so _enter does not run again. Filter tweaks the
		 * user makes during a session carry into the next session; only get
		 * swapped to the matching profile on _enter and back to the default
		 * profile on _exit.
		 *
		 * Late-arrival gate inside fetchFilterPreference prevents an
		 * in-flight default fetch from clobbering the matching profile when
		 * the user lands directly on `?match=...`.
		 */
		_enter: () => {
			capturePreMatchSnapshot()
			applyCachedProfile('match')
			void fetchFilterPreference(MATCH_MAPLIBRE_FILTER_KEY)
			enforce_sidebar()
		},
		_exit: (meta) => {
			// leaving matching mode invalidates any open popup (filter state diverges,
			// the marker the popup is anchored to may no longer be rendered)
			appState.closePopup()
			restorePreMatchSnapshot()
			appState.matching?.destroy?.()
			appState.matching = null
			clearMatchParam()
			breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			/**
			 * restore the marker URL only when the destination state matches
			 * the captured previous_state. the destination's marker_handler
			 * already wrote `?<type>=<id>` via syncMarkerParam; running
			 * restore_prev_state with a divergent previous_state would
			 * overwrite that with a stale type/id pair
			 */
			if (meta.to === appState.previous_state) {
				restore_prev_state()
			}
		},
		reset: reset,
		...marker_handler,
		...fak_handler, // TODO: confirm why FaK should be allowed during matching
		set_minilot: 'minilot',
		set_matching: initializeMatching as Action<States>
	},
	minilot: {
		_enter: () => {
			enforce_sidebar()
		},
		_exit: (event) => {
			// if moving from minilot to matching, preserve minilotData so that we can restore if we return
			if (event.to === 'matching') {
				return
			}
			appState.clearMinilotData()
		},
		reset: reset,
		...global_handler
	},
	request: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	returnFreight: {
		_enter: () => {
			enforce_sidebar()
		},
		reset: reset,
		...global_handler
	},
	fak: {
		_enter: (meta) => {
			capture_prev_state(meta.from as States)
			enforce_fak()
			// appState.openFak(event.projectId, event.lotId)
		},
		_exit: (meta) => {
			// TODO: here we could restore the previous state
			capture_prev_state(meta.to as States)
		},
		close_fak: close_fak,
		reset: reset
	}
})

function clear_all_states() {
	if (is_initial_reset) {
		is_initial_reset = false
		return
	}
	appState.deselectMarker()
	appState.deselectChildren()
	fakState.close()
}

function soft_reset() {
	appState.isRoutePlanerOpened = false
	appState.isSlideInOpened = false
	appState.isFilterOpened = false
}

function reset(): States {
	soft_reset()
	appState.route.clearCoordinates()
	appState.clearAdHocMarkers()
	appState.history.clear()
	appState.clearMinilotData()
	breadcrumbs.clear(MAP_BREADCRUMBS_CONTEXT)
	clearAllMarkerParams()
	clearMatchParam()
	syncFakParams(null, null)
	return 'idle'
}
